/**
 * Canonical mask registry derived from menuItems + a small DETAIL override map.
 * Search/open use this index; do not duplicate hundreds of hard-coded mask defs.
 */

import { menuItems } from './menuItems';
import { isItemVisible } from './menuSearch';

export const MASK_TYPES = Object.freeze({
  LIST: 'LIST',
  DETAIL: 'DETAIL',
  FORM: 'FORM',
  PREVIEW: 'PREVIEW',
  DASHBOARD: 'DASHBOARD',
  OTHER: 'OTHER',
});

/** Product openability classification for search/open. */
export const OPENABILITY = Object.freeze({
  GENERIC_DETAIL: 'GENERIC_DETAIL',
  SPECIAL_DETAIL: 'SPECIAL_DETAIL',
  FORM_WITH_CONTEXT: 'FORM_WITH_CONTEXT',
  NOT_DETAIL: 'NOT_DETAIL',
  NOT_SAFE_TO_OPEN_DIRECTLY: 'NOT_SAFE_TO_OPEN_DIRECTLY',
});

const paged = (endpoint, query = {}) => ({
  listEndpoint: endpoint,
  listResponseStyle: 'paged',
  listQuery: { page: 1, size: 50, ...query },
  navigationStyle: 'navigation_context',
});

const reduxArray = (endpoint, loadReduxAction) => ({
  listEndpoint: endpoint,
  listResponseStyle: 'array',
  loadReduxAction,
  navigationStyle: 'redux_array',
});

/**
 * Exceptional detail→parent relationships for masks that are not menu leaves
 * (or need bootstrap metadata). Keep ONLY exceptional relationships here.
 *
 * navigationStyle:
 *  - redux_array: load full parent list into Redux; detail arrows use createArrayNavigateHandlers
 *  - navigation_context: page-1 parent list; detail arrows use /navigate + navigationContext
 *  - self: detail loads its own sibling universe after open
 *
 * propStrategy:
 *  - entity (default): props[entityProp] = row
 *  - customer: { customerId, customerRow }
 *  - companyRow: { companyRow }
 *  - ticketId: { ticketId: row.id }
 *  - checklist: { checklist_record, recordId }
 */
export const DETAIL_MASK_OVERRIDES = Object.freeze({
  gset002: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'DepartmentAdd',
    translationKey: 'MaskLabel.gset002',
    tabTranslationKey: 'DepartmentList.Tab.Edit',
    parentMask: 'gset001',
    idField: 'id',
    entityProp: 'department',
    requiredContext: true,
    openType: 'edit',
    ...reduxArray('/api/departments', { type: 'load_departments', payloadKey: 'newDepartments' }),
  },
  gset004: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'EmployeeAdd',
    translationKey: 'MaskLabel.gset004',
    tabTranslationKey: 'EmployeeList.Tab.Edit',
    parentMask: 'gset003',
    idField: 'id',
    entityProp: 'employee',
    requiredContext: true,
    openType: 'edit',
    ...reduxArray('/api/employees', { type: 'load_employees', payloadKey: 'newEmployees' }),
  },
  gset006: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'UserAdd',
    translationKey: 'MaskLabel.gset006',
    tabTranslationKey: 'UserList.Tab.Edit',
    parentMask: 'gset005',
    idField: 'id',
    entityProp: 'user',
    requiredContext: true,
    openType: 'edit',
    ...reduxArray('/api/users', { type: 'load_users', payloadKey: 'newUsers' }),
  },
  gset012: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'RoleAdd',
    translationKey: 'MaskLabel.gset012',
    tabTranslationKey: 'RoleList.Tab.Edit',
    parentMask: 'gset011',
    idField: 'id',
    entityProp: 'role',
    requiredContext: true,
    openType: 'edit',
    ...reduxArray('/api/roles', { type: 'load_roles', payloadKey: 'newRoles' }),
  },
  gset014: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'MachineAdd',
    translationKey: 'MaskLabel.gset014',
    tabTranslationKey: 'MachineList.Tab.Edit',
    parentMask: 'gset013',
    idField: 'id',
    entityProp: 'machine',
    requiredContext: true,
    openType: 'edit',
    ...reduxArray('/api/machines?show_excluded_machines=0', { type: 'load_machines', payloadKey: 'newMachines' }),
  },
  gset016: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'MachineTypeAdd',
    translationKey: 'MaskLabel.gset016',
    tabTranslationKey: 'MachineTypeList.Tab.Edit',
    parentMask: 'gset015',
    idField: 'id',
    entityProp: 'machine_type',
    requiredContext: true,
    openType: 'edit',
    ...reduxArray('/api/machine_types', { type: 'load_machine_types', payloadKey: 'newMachineTypes' }),
  },
  trng004: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'TrainingCardDetail',
    translationKey: 'MaskLabel.trng004',
    tabTranslationKey: 'MaskLabel.trng004',
    parentMask: 'trng002',
    idField: 'id',
    entityProp: 'employee',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/training_cards/paged', { show_deactivated: '0', show_inactive_employees: '0' }),
  },
  trng013: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'TrainingRecordDetail',
    translationKey: 'MaskLabel.trng013',
    tabTranslationKey: 'Util.Tab.EmployeeTraining',
    parentMask: 'trng011',
    idField: 'id',
    entityProp: 'employee',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/training_cards/paged', { show_deactivated: '0', show_inactive_employees: '0' }),
  },
  trng015: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'TrainingEmployeeDetail',
    translationKey: 'MaskLabel.trng015',
    tabTranslationKey: 'TrainingEmployeeList.Text.Zoznam_zamestnancov_skolenia',
    parentMask: 'trng014',
    idField: 'id',
    entityProp: 'training_type',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/training_types/paged'),
  },
  mntn017: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'MaintenanceCardDetail',
    translationKey: 'MaskLabel.mntn017',
    tabTranslationKey: 'Maintenance.EditMachineCard',
    parentMask: 'mntn015',
    idField: 'id',
    entityProp: 'machine',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/machine_cards/paged', { show_deactivated: '0', show_excluded_machines: '0' }),
  },
  mntn020: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'MaintenanceRecordDetail',
    translationKey: 'MaskLabel.mntn020',
    tabTranslationKey: 'Print.Title.MaintenanceRecordDetail',
    parentMask: 'mntn018',
    idField: 'id',
    entityProp: 'machine',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/machine_cards/paged', { show_deactivated: '0', show_excluded_machines: '0' }),
  },
  mntn022: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'MaintenanceMachineDetail',
    translationKey: 'MaskLabel.mntn022',
    tabTranslationKey: 'MaintenanceMachineList.Text.Zoznam_podla_planovanych_ukonov',
    parentMask: 'mntn021',
    idField: 'id',
    entityProp: 'maintenance_type',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/maintenance_types/paged'),
  },
  mntn025: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'MaintenanceTaskRecordDetail',
    translationKey: 'MaskLabel.mntn025',
    tabTranslationKey: 'Print.Title.MaintenanceTaskRecordDetail',
    parentMask: 'mntn024',
    idField: 'id',
    entityProp: 'machine',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/machines/paged', { show_excluded_machines: '0' }),
  },
  mntn031: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'MaintenanceNeedRecordDetail',
    translationKey: 'MaskLabel.mntn031',
    tabTranslationKey: 'Print.Title.MaintenanceNeedRecordDetail',
    parentMask: 'mntn030',
    idField: 'id',
    entityProp: 'machine',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/machines/paged', { show_excluded_machines: '0' }),
  },
  mntn028: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'MaintenanceCheckRecordDetail',
    translationKey: 'MaskLabel.mntn028',
    tabTranslationKey: 'Maintenance.MachineChecklists',
    parentMask: 'mntn027',
    idField: 'id',
    entityProp: 'machine',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/machines/paged', { show_excluded_machines: '0' }),
  },
  mntn038: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'MaintenanceMachineStateDetail',
    translationKey: 'MaskLabel.mntn038',
    tabTranslationKey: 'Print.Title.MaintenanceMachineStateDetail',
    parentMask: 'mntn037',
    idField: 'id',
    entityProp: 'machine',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/machines/outage-state/paged'),
    navigationStyle: 'self',
  },
  cust012: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'CustomerCentralDatabaseAdd',
    translationKey: 'MaskLabel.cust012',
    tabTranslationKey: 'CustomerCentralDatabaseAdd.Title',
    parentMask: 'cust011',
    permission: 'access customer_management',
    idField: 'id',
    entityProp: 'companyRow',
    propStrategy: 'companyRow',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/customer_evaluations/central_db/paged'),
  },
  cust014: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'CustomerMyCustomersDetail',
    translationKey: 'MaskLabel.cust014',
    tabTranslationKey: 'CustomerMyCustomersDetail.Title',
    parentMask: 'cust013',
    permission: 'access customer_management',
    idField: 'id',
    entityProp: 'customerRow',
    propStrategy: 'customer',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/customer_evaluations/paged', { mine_only: '1' }),
    navigationStyle: 'self',
  },
  cust017: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'CustomerAllPartnersDetail',
    translationKey: 'MaskLabel.cust017',
    tabTranslationKey: 'CustomerAllPartnersDetail.Title',
    parentMask: 'cust016',
    permission: 'access customer_management-complete-list',
    idField: 'id',
    entityProp: 'customerRow',
    propStrategy: 'customer',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/customer_evaluations/all_partners/paged'),
    navigationStyle: 'self',
  },
  cust026: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'CustomerMyBusinessByCustomerDetail',
    translationKey: 'MaskLabel.cust026',
    tabTranslationKey: 'CustomerMyBusinessByCustomerDetail.Title',
    parentMask: 'cust025',
    permission: 'access customer_management',
    idField: 'id',
    entityProp: 'customerRow',
    propStrategy: 'customer',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/customer_evaluations/my_business/by_customer/paged'),
    navigationStyle: 'self',
  },
  cust029: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'CustomerAllBusinessByCustomerDetail',
    translationKey: 'MaskLabel.cust029',
    tabTranslationKey: 'CustomerAllBusinessByCustomerDetail.Title',
    parentMask: 'cust028',
    permission: 'access customer_management-complete-list',
    idField: 'id',
    entityProp: 'customerRow',
    propStrategy: 'customer',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/customer_evaluations/all_business/by_customer/paged'),
    navigationStyle: 'self',
  },
  cust032: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'CustomerMyAcquisitionByCustomerDetail',
    translationKey: 'MaskLabel.cust032',
    tabTranslationKey: 'CustomerMyAcquisitionByCustomerDetail.Title',
    parentMask: 'cust031',
    permission: 'access customer_management',
    idField: 'id',
    entityProp: 'customerRow',
    propStrategy: 'customer',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/customer_evaluations/my_acquisitions/by_customer/paged'),
    navigationStyle: 'self',
  },
  cust035: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'CustomerAllAcquisitionByCustomerDetail',
    translationKey: 'MaskLabel.cust035',
    tabTranslationKey: 'CustomerAllAcquisitionByCustomerDetail.Title',
    parentMask: 'cust034',
    permission: 'access customer_management-complete-list',
    idField: 'id',
    entityProp: 'customerRow',
    propStrategy: 'customer',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/customer_evaluations/all_acquisitions/by_customer/paged'),
    navigationStyle: 'self',
  },
  // Menu-canonical: cust007 → CustomerStatesAdd (cust009). CustomerLevelsAdd also uses cust009 (collision).
  cust009: {
    openability: OPENABILITY.FORM_WITH_CONTEXT,
    type: MASK_TYPES.FORM,
    page: 'CustomerStatesAdd',
    translationKey: 'MaskLabel.cust009',
    tabTranslationKey: 'CustomerStatesAdd.TabDetail',
    parentMask: 'cust007',
    permission: 'access customer_management-setup',
    idField: 'id',
    entityProp: 'crm_state',
    requiredContext: true,
    openType: 'edit',
    ...reduxArray('/api/crm_states', { type: 'load_crm_states', payloadKey: 'newCrmStates' }),
  },
  cust010: {
    openability: OPENABILITY.FORM_WITH_CONTEXT,
    type: MASK_TYPES.FORM,
    page: 'CustomerTradersAdd',
    translationKey: 'MaskLabel.cust010',
    tabTranslationKey: 'CustomerTradersAdd.Title',
    parentMask: 'cust008',
    permission: 'access customer_management-setup',
    idField: 'id',
    entityProp: 'crm_trader',
    requiredContext: true,
    openType: 'edit',
    ...reduxArray('/api/crm_traders', { type: 'load_crm_traders', payloadKey: 'newCrmTraders' }),
  },
  cust024: {
    openability: OPENABILITY.FORM_WITH_CONTEXT,
    type: MASK_TYPES.FORM,
    page: 'CustomerStepsAdd',
    translationKey: 'MaskLabel.cust024',
    tabTranslationKey: 'CustomerStepsAdd.Title',
    parentMask: 'cust023',
    permission: 'access customer_management-setup',
    idField: 'id',
    entityProp: 'crm_step',
    requiredContext: true,
    openType: 'edit',
    ...reduxArray('/api/crm_steps', { type: 'load_crm_steps', payloadKey: 'newCrmSteps' }),
  },
  calb011: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'CalibrationCompanyCertRecordDetail',
    translationKey: 'MaskLabel.calb011',
    tabTranslationKey: 'Calibration.CompanyCertValidity',
    parentMask: 'calb010',
    idField: 'id',
    entityProp: 'calibration_company',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/companies/paged', {
      type: 'calibration',
      show_excluded_companies: '0',
      show_deactivated_certs: '0',
    }),
    navigationStyle: 'self',
  },
  qlty029: {
    openability: OPENABILITY.GENERIC_DETAIL,
    type: MASK_TYPES.DETAIL,
    page: 'OtkCompanyCertRecordDetail',
    translationKey: 'MaskLabel.qlty029',
    tabTranslationKey: 'Otk.PartnerDocumentations',
    parentMask: 'qlty028',
    idField: 'id',
    entityProp: 'otk_company',
    requiredContext: true,
    openType: 'edit',
    ...paged('/api/company_certificates/paged_by_company', {
      type: 'otk',
      show_excluded_companies: '0',
      show_deactivated_certs: '0',
    }),
    navigationStyle: 'self',
  },
});

/**
 * Searchable but not directly openable — fall back to canonical parent/list.
 * Still shown in mask search when permitted.
 */
export const CONTEXT_ONLY_MASKS = Object.freeze({
  cust020: {
    openability: OPENABILITY.NOT_SAFE_TO_OPEN_DIRECTLY,
    type: MASK_TYPES.DETAIL,
    page: 'CrmCommunicationTicketDetail',
    translationKey: 'MaskLabel.cust020',
    tabTranslationKey: 'CrmCommunication.Tab.TicketDetail',
    parentMask: 'cust027',
    permission: 'access customer_management',
    requiredContext: true,
    openStrategy: 'parent_fallback',
    reasonKey: 'MaskSearch.RequiresContext',
  },
  chck006: {
    openability: OPENABILITY.NOT_SAFE_TO_OPEN_DIRECTLY,
    type: MASK_TYPES.DETAIL,
    page: 'ChecklistStateDetail',
    translationKey: 'MaskLabel.chck006',
    tabTranslationKey: 'Checklist.RecordTab',
    parentMask: 'chck005',
    permission: 'edit checklists',
    requiredContext: true,
    openStrategy: 'parent_fallback',
    reasonKey: 'MaskSearch.RequiresContext',
  },
  // Orphaned menu-comment codes — never dispatched as create_new_tab in app code.
  chck010: {
    openability: OPENABILITY.NOT_DETAIL,
    type: MASK_TYPES.OTHER,
    page: 'ChecklistRecordMyList',
    translationKey: 'MaskLabel.chck010',
    tabTranslationKey: 'Menu.Checklists.ZoznamChecklistov',
    parentMask: 'chck009',
    permission: 'access checklists',
    requiredContext: true,
    openStrategy: 'parent_fallback',
    reasonKey: 'MaskSearch.RequiresContext',
  },
  chck012: {
    openability: OPENABILITY.NOT_DETAIL,
    type: MASK_TYPES.OTHER,
    page: 'ChecklistRecordAllList',
    translationKey: 'MaskLabel.chck012',
    tabTranslationKey: 'Menu.Checklists.UplnyZoznam',
    parentMask: 'chck011',
    permission: 'access checklists-complete-list',
    requiredContext: true,
    openStrategy: 'parent_fallback',
    reasonKey: 'MaskSearch.RequiresContext',
  },
});

/** @deprecated R76: all R74 unsupported masks are now classified (0 remaining). */
export const UNSUPPORTED_DETAIL_MASKS = Object.freeze([]);

function classifyMaskType(page, explicitType) {
  if (explicitType) return explicitType;
  const p = String(page || '');
  if (/Preview/i.test(p)) return MASK_TYPES.PREVIEW;
  if (/Dashboard|SalesEvaluation|Evaluation|Statistics|Graph/i.test(p)) return MASK_TYPES.DASHBOARD;
  if (/Detail$/i.test(p)) return MASK_TYPES.DETAIL;
  if (/Add$/i.test(p)) return MASK_TYPES.FORM;
  if (/List$/i.test(p)) return MASK_TYPES.LIST;
  return MASK_TYPES.OTHER;
}

function walkMenuLeaves(items, parentPermission, acc, parentTitle) {
  for (const item of items || []) {
    // Collect permission metadata only; visibility is applied at search time with real perms.
    const effectivePermission =
      item.permission !== undefined && item.permission !== null
        ? item.permission
        : parentPermission;

    if (Array.isArray(item.children)) {
      walkMenuLeaves(
        item.children,
        effectivePermission,
        acc,
        item.title || item.page || parentTitle,
      );
      continue;
    }
    if (!item.mask || !item.page) continue;
    const code = String(item.mask).toLowerCase();
    acc.set(code, {
      maskCode: code,
      type: classifyMaskType(item.page),
      openability: 'SIMPLE',
      translationKey: item.tabName || item.title || item.page,
      page: item.page,
      permission: effectivePermission,
      parentMask: null,
      idField: null,
      entityProp: null,
      requiredContext: false,
      openStrategy: 'simple',
      props: item.props ?? {},
      menuId: item.id,
      parentTitle: parentTitle || null,
      source: 'menu',
    });
  }
  return acc;
}

/**
 * Build (or return cached) full mask registry map: maskCode → entry.
 */
let _cachedRegistry = null;

function mergeOverrideEntry(map, code, override, source) {
  const parent = map.get(String(override.parentMask || '').toLowerCase()) || null;
  const existing = map.get(code);
  map.set(code, {
    maskCode: code,
    type: override.type || MASK_TYPES.DETAIL,
    openability: override.openability || OPENABILITY.GENERIC_DETAIL,
    translationKey: override.translationKey || existing?.translationKey || `MaskLabel.${code}`,
    tabTranslationKey: override.tabTranslationKey || override.translationKey,
    page: override.page || existing?.page,
    permission: override.permission ?? parent?.permission ?? existing?.permission ?? null,
    parentMask: override.parentMask || null,
    idField: override.idField || 'id',
    entityProp: override.entityProp || null,
    propStrategy: override.propStrategy || 'entity',
    requiredContext: override.requiredContext !== false,
    openStrategy: override.openStrategy || 'detail',
    reasonKey: override.reasonKey || null,
    props: {},
    menuId: existing?.menuId || null,
    parentTitle: parent?.translationKey || existing?.parentTitle || null,
    source,
    openType: override.openType || 'edit',
    listEndpoint: override.listEndpoint || null,
    listResponseStyle: override.listResponseStyle || 'array',
    listQuery: override.listQuery || null,
    loadReduxAction: override.loadReduxAction || null,
    navigationStyle: override.navigationStyle || 'redux_array',
  });
}

export function buildMaskRegistry(items = menuItems) {
  const map = walkMenuLeaves(items, null, new Map(), null);

  for (const [code, override] of Object.entries(DETAIL_MASK_OVERRIDES)) {
    mergeOverrideEntry(map, code, override, 'detail_override');
  }
  for (const [code, override] of Object.entries(CONTEXT_ONLY_MASKS)) {
    mergeOverrideEntry(map, code, override, 'context_only');
  }

  return map;
}

export function getCoverageStats(registry = getMaskRegistry()) {
  const all = Array.from(registry.values());
  const byOpenability = {};
  all.forEach((e) => {
    const key = e.openability || (e.requiredContext ? OPENABILITY.GENERIC_DETAIL : 'SIMPLE');
    byOpenability[key] = (byOpenability[key] || 0) + 1;
  });
  const simple = all.filter((e) => !e.requiredContext).length;
  const generic = all.filter((e) => e.openability === OPENABILITY.GENERIC_DETAIL).length;
  const special = all.filter((e) => e.openability === OPENABILITY.SPECIAL_DETAIL).length;
  const formCtx = all.filter((e) => e.openability === OPENABILITY.FORM_WITH_CONTEXT).length;
  const contextOnly = all.filter(
    (e) =>
      e.openability === OPENABILITY.NOT_SAFE_TO_OPEN_DIRECTLY ||
      e.openability === OPENABILITY.NOT_DETAIL ||
      e.openStrategy === 'parent_fallback',
  ).length;
  return {
    total: all.length,
    simple_openable: simple,
    generic_details: generic,
    special_resolved: special + formCtx,
    context_only: contextOnly,
    unsupported: UNSUPPORTED_DETAIL_MASKS.length,
    byOpenability,
  };
}

export function getMaskRegistry() {
  if (!_cachedRegistry) _cachedRegistry = buildMaskRegistry();
  return _cachedRegistry;
}

/** Test helper — clear memoized registry after menu/override changes. */
export function resetMaskRegistryCache() {
  _cachedRegistry = null;
}

export function getMaskEntry(maskCode) {
  if (!maskCode) return null;
  return getMaskRegistry().get(String(maskCode).toLowerCase()) || null;
}

export function listMaskEntries() {
  return Array.from(getMaskRegistry().values());
}

/**
 * Permission check reuses menu visibility contract via synthetic item shape.
 */
export function isMaskEntryVisible(entry, userPermissions) {
  if (!entry) return false;
  const synthetic = {
    permission: entry.permission,
    page: entry.page,
    title: entry.translationKey,
  };
  return isItemVisible(synthetic, userPermissions, true);
}

export function normalizeMaskCode(code) {
  return String(code || '').trim().toLowerCase();
}

/** Query looks like a mask code (exact or partial), not free text with spaces/diacritics. */
export function isMaskCodeQuery(query) {
  const q = String(query || '').trim();
  return /^[a-z]{2,12}\d{0,6}$/i.test(q);
}
